export const TAX_STORAGE_KEY = 'alfinder_tax';
export const TAX_TIMESTAMP_KEY = 'alfinder_tax_timestamp';
export const CURRENCY_STORAGE_KEY = 'alfinder_currency';

/**
 * How long a calibrated multiplier stays trusted.
 *
 * This was a week. The multiplier is a RATIO measured between two independent
 * sources — the live storefront's `regular_price` and our indexed price — so
 * anything that knocks those out of step (a price change we haven't synced, a
 * stale index entry) is baked into the cached value and applied store-wide to
 * every price the shopper sees, on every surface. A week of that is invisible
 * to everyone except the one shopper holding it; a day bounds the damage while
 * still costing only one recalibration per shopper per day.
 */
export const TAX_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Above this, a measured multiplier is treated as un-cacheable.
 *
 * It is NOT a rejection: a store whose real rate lands above the band still
 * renders with the measured value (see getTaxWithRetry), so a legitimately high
 * VAT is never turned into a wrong price. It only refuses to PERSIST such a
 * value, so it re-measures on the next page load instead of following the
 * shopper around. No national VAT exceeds 27% (Hungary), so a multiplier past
 * 1.3 is far more likely a measurement artifact than a tax — 1.3225 (= 1.15
 * applied twice) is exactly the shape of one.
 */
export const MAX_PERSISTABLE_TAX = 1.3;

/**
 * How close two independently measured multipliers must be to corroborate each
 * other. Every product in a store shares its tax rate, so real measurements
 * land within rounding noise of one another; 0.5% absorbs the float artifacts
 * of prices stored to 2-3 decimals without accepting a genuine disagreement.
 */
export const TAX_AGREEMENT_TOLERANCE = 0.005;

/**
 * Where a calibration that produced NOTHING is remembered.
 *
 * Deliberately a separate key from TAX_STORAGE_KEY: a failure is not a
 * multiplier, and must never be readable as one.
 */
export const TAX_FAILURE_KEY = 'alfinder_tax_failed_at';

/**
 * How long that failure is remembered.
 *
 * Only a failure the store will REPEAT is remembered — see
 * answeredButUncalibratable. Probes that never answered don't count: Salla feeds
 * routinely list products Salla itself has deleted, and those 404s say nothing
 * about whether the NEXT search's products will calibrate.
 *
 * Calibration sits on the critical path of every search — searchProducts awaits
 * it before returning results — and costs a storefront round-trip per candidate.
 * A success is cached; a failure was not, so every search re-ran the entire
 * candidate sweep and the shopper paid seconds of dead time for an answer that
 * got thrown away either way. segadty.com is the store that exposed it: its
 * indexed prices are in SAR while its /ar-us/ storefront quotes USD, so every
 * probe measures a ratio of ~0.27 — below the `tax >= 1` floor — and NO
 * candidate can ever calibrate. Five probes, ~7s, on every single search.
 *
 * Remembering costs nothing in price accuracy: a failed calibration renders
 * untaxed prices, and it renders exactly those same prices during the window.
 * It only stops us re-buying the same answer. Kept short so a store that failed
 * transiently (a flaky storefront API) starts taxing again soon.
 */
export const TAX_FAILURE_TTL_MS = 10 * 60 * 1000;

/**
 * How long a shopper may wait for calibration before we render without it.
 *
 * The probes are storefront calls we don't control; measured on segadty.com they
 * ran 0.9-2.9s EACH. Correct prices matter, but a search box that sits spinning
 * for eight seconds has already lost the shopper — and past this budget we are
 * usually waiting on a calibration that will fail anyway. Overrunning probes are
 * not wasted: they keep running and still persist a corroborated multiplier, so
 * a store that is merely slow calibrates for the next search instead of stalling
 * this one.
 */
export const TAX_CALIBRATION_BUDGET_MS = 2500;

/** How many measurements it takes before one can corroborate another. */
const CORROBORATION_QUORUM = 2;

const isPersistableTax = (tax: number): boolean =>
  !isNaN(tax) && isFinite(tax) && tax >= 1 && tax <= MAX_PERSISTABLE_TAX;

const agreesWith = (a: number, b: number): boolean =>
  Math.abs(a - b) / a <= TAX_AGREEMENT_TOLERANCE;

export const getSallaProductDetails = async (productId: number) => {
  const api = window.salla?.api;
  // Tax calibration is a silent background probe: we only read the sampled
  // product's regular_price to derive the store-wide tax multiplier. When that
  // product is out of stock or deleted, Salla's global API error handler pops a
  // SweetAlert toast (e.g. "نفذت الكمية") that leaks to shoppers on page load.
  // Route the probe through Salla's official notifier-suppression wrapper so a
  // failed probe never surfaces a user-facing toast. `withoutNotifier` still
  // rejects on error, so the retry/fallback logic in getTaxWithRetry is
  // unaffected. Older Twilight builds predate it, so fall back to a direct call.
  const priceData =
    typeof api?.withoutNotifier === 'function'
      ? await api.withoutNotifier(() => api.product.getDetails(productId))
      : await api?.product.getDetails(productId);
  return priceData.data.regular_price;
};

export const getZidProductDetails = async (productId: string) => {
  const has_vitrin = window.vitrin == true;
  if (has_vitrin) {
    const response = await window.zid?.products.get(productId);
    return response.price;
  } else {
    const response = await window.zid?.store.product.fetch(productId);
    return response.data.product.price;
  }
};

export const calculateTax = (
  priceWithTaxes: number,
  priceWithoutTaxes: number,
) => {
  // Validate inputs
  if (
    !isFinite(priceWithTaxes) ||
    !isFinite(priceWithoutTaxes) ||
    priceWithoutTaxes === 0 ||
    priceWithTaxes <= 0 ||
    priceWithoutTaxes <= 0
  ) {
    console.warn('Invalid tax calculation inputs:', {
      priceWithTaxes,
      priceWithoutTaxes,
    });
    return NaN;
  }

  const tax = priceWithTaxes / priceWithoutTaxes;

  // Validate result - tax must be at least 1 (no tax or higher)
  if (!isFinite(tax) || tax < 1) {
    console.warn('Invalid tax calculation result (must be >= 1):', tax);
    return NaN;
  }

  return tax;
};

export const getPriceWithTaxes = async (
  storeType: string,
  productId: string | number,
) => {
  switch (storeType) {
    case 'salla':
      return getSallaProductDetails(Number(productId));
    case 'zid':
      return getZidProductDetails(productId.toString());
    default:
      return null;
  }
};

export const getTaxWithExpiry = (): number | null => {
  const storedTax = localStorage.getItem(TAX_STORAGE_KEY);
  const timestamp = localStorage.getItem(TAX_TIMESTAMP_KEY);

  console.log('Stored tax:', storedTax);
  console.log('Stored timestamp:', timestamp);

  if (!storedTax || !timestamp) {
    return null;
  }

  const now = new Date().getTime();
  console.log('Time difference (ms):', now - parseInt(timestamp));
  console.log('Tax TTL in ms:', TAX_TTL_MS);

  if (now - parseInt(timestamp) > TAX_TTL_MS) {
    // Tax value has expired, clear it
    localStorage.removeItem(TAX_STORAGE_KEY);
    localStorage.removeItem(TAX_TIMESTAMP_KEY);
    return null;
  }

  const parsedTax = parseFloat(storedTax);

  // Reject on the same terms we refuse to write: >= 1, and inside the
  // believable band. Checking the ceiling on READ as well as on write is what
  // heals a browser that an older build already poisoned — it drops the bad
  // value on the next page load instead of honouring it until it expires.
  if (!isPersistableTax(parsedTax)) {
    console.log(
      'Stored tax is invalid (NaN, infinite, below 1, or above the believable band), clearing tax data',
    );
    localStorage.removeItem(TAX_STORAGE_KEY);
    localStorage.removeItem(TAX_TIMESTAMP_KEY);
    return null;
  }

  return parsedTax;
};

export const storeTaxWithExpiry = (tax: number) => {
  // Validate tax before storing (must be >= 1 and inside the believable band).
  // An out-of-band value is deliberately dropped rather than written: the
  // caller still uses it for the current page, it just never gets to outlive it.
  if (!isPersistableTax(tax)) {
    console.warn(
      'Refusing to persist tax value (must be >= 1 and <= ' +
        MAX_PERSISTABLE_TAX +
        '):',
      tax,
    );
    return;
  }

  const timestamp = new Date().getTime();
  localStorage.setItem(TAX_STORAGE_KEY, tax.toString());
  localStorage.setItem(TAX_TIMESTAMP_KEY, timestamp.toString());
  // A multiplier we can persist settles the question — including for a probe
  // that overran its budget and landed after we had already given up.
  localStorage.removeItem(TAX_FAILURE_KEY);
  // Add logging to verify storage
  console.log('Storing tax:', tax);
  console.log('Storing timestamp:', timestamp);
};

export const handleCurrencyChange = (currentCurrency: string) => {
  const lastCurrency = localStorage.getItem(CURRENCY_STORAGE_KEY);

  console.log('Currency check:', {
    current: currentCurrency,
    last: lastCurrency,
  });

  if (lastCurrency && lastCurrency !== currentCurrency) {
    console.log('Currency changed, clearing tax data');
    localStorage.removeItem(TAX_STORAGE_KEY);
    localStorage.removeItem(TAX_TIMESTAMP_KEY);
    // A calibration that failed against the OLD currency says nothing about the
    // new one — the ratio is measured between two prices, and one of them just
    // changed. Re-probe rather than inherit the old verdict.
    localStorage.removeItem(TAX_FAILURE_KEY);
  }

  localStorage.setItem(CURRENCY_STORAGE_KEY, currentCurrency);
};

/** A single tax-calibration candidate: probe this product's price. */
export interface TaxSample {
  productId: string | number;
  price: number;
}

/**
 * Whether calibration failed recently enough that running it again would only
 * buy the same nothing. See TAX_FAILURE_TTL_MS.
 */
const hasRecentCalibrationFailure = (): boolean => {
  const failedAt = localStorage.getItem(TAX_FAILURE_KEY);
  if (!failedAt) {
    return false;
  }

  const age = new Date().getTime() - parseInt(failedAt, 10);
  if (isNaN(age) || !isFinite(age) || age < 0 || age > TAX_FAILURE_TTL_MS) {
    localStorage.removeItem(TAX_FAILURE_KEY);
    return false;
  }
  return true;
};

const rememberCalibrationFailure = () => {
  localStorage.setItem(TAX_FAILURE_KEY, new Date().getTime().toString());
};

/**
 * What one probe learned.
 *
 * `answered` is tracked separately from `tax` because the two failures mean
 * completely different things. A candidate that ANSWERED and still produced no
 * usable multiplier says something about the STORE — its storefront price and
 * our indexed price don't relate the way calibration assumes. A candidate that
 * never answered says only that this one product is gone. Only the first is
 * worth remembering; see TAX_FAILURE_TTL_MS.
 */
interface ProbeResult {
  /** The storefront returned a price for this product. */
  answered: boolean;
  /** A usable multiplier, or null when the answer couldn't yield one. */
  tax: number | null;
}

/**
 * Probe ONE candidate. Never throws — every way a candidate can fail comes back
 * as a ProbeResult, so a dead product is simply a candidate that didn't answer.
 */
const measureCandidate = async (
  storeType: string,
  { productId, price }: TaxSample,
): Promise<ProbeResult> => {
  try {
    const priceWithTaxes = await getPriceWithTaxes(storeType, productId);
    if (priceWithTaxes === null || priceWithTaxes === undefined) {
      return { answered: false, tax: null };
    }

    const tax = calculateTax(priceWithTaxes, price);
    if (isNaN(tax) || !isFinite(tax) || tax < 1) {
      // It answered — the price just doesn't relate to ours as a tax rate.
      return { answered: true, tax: null };
    }
    return { answered: true, tax };
  } catch (error) {
    // Probe failed (e.g. 404 on a deleted product) — this candidate is out.
    console.warn('Tax probe failed for product', productId, error);
    return { answered: false, tax: null };
  }
};

/**
 * The multipliers actually measured, in CANDIDATE order — never completion
 * order. Which measurement corroborates which, and so the value the whole store
 * gets priced with, must not depend on the order the storefront answered in.
 * Candidates that failed, or that haven't answered yet, are simply absent.
 */
const takenMeasurements = (probes: ProbeResult[]): number[] =>
  probes.reduce<number[]>((taken, probe) => {
    if (probe.tax !== null) {
      taken.push(probe.tax);
    }
    return taken;
  }, []);

/**
 * Whether the store answered and STILL couldn't be calibrated.
 *
 * This is the difference between "this store cannot be calibrated" (segadty.com:
 * every product priced in a different currency from our index, so every ratio
 * lands below 1) and "these particular products are gone" (a Salla store whose
 * index still lists deleted products, so the probes 404). Only the former will
 * still be true in ten minutes.
 */
const answeredButUncalibratable = (probes: ProbeResult[]): boolean =>
  probes.some((probe) => probe.answered && probe.tax === null);

/**
 * The first multiplier that a LATER candidate independently agrees with, or
 * null while nothing has been corroborated yet.
 */
const firstCorroborated = (probes: ProbeResult[]): number | null => {
  const taken = takenMeasurements(probes);
  for (let later = 0; later < taken.length; later += 1) {
    for (let earlier = 0; earlier < later; earlier += 1) {
      if (agreesWith(taken[earlier], taken[later])) {
        return taken[earlier];
      }
    }
  }
  return null;
};

/**
 * Probe candidates until TWO of them agree; null if none ever do.
 *
 * Trying DIFFERENT products — rather than retrying the same one — is what makes
 * calibration resilient: the widget backend can still list a product Salla has
 * deleted, so its probe 404s. A single dead sample used to fail calibration
 * outright and leave every price untaxed; now we fall through to the next.
 *
 * Corroboration is the point: the multiplier is applied to EVERY price on EVERY
 * surface, so letting one product decide it store-wide means any single anomaly
 * — a price changed on Salla we haven't synced, an index entry gone stale —
 * silently becomes a store-wide error. Two products that independently measure
 * the same rate can't both be anomalous in the same direction by accident.
 *
 * Candidates are probed in two WAVES rather than one at a time. Corroboration
 * needs two measurements, so the first wave asks for exactly two; only if those
 * fail to agree do we spend a second round-trip on the rest. That issues no more
 * requests than probing one-by-one ever did, while collapsing up to five
 * SEQUENTIAL round-trips into at most two — the serialization is what turned a
 * 700ms search response into an eight-second wait on segadty.com.
 *
 * `probes` is passed in rather than kept local, and each probe writes its own
 * slot the moment it answers, so a caller that stops waiting
 * (calibrateWithinBudget) can still read every measurement that landed before it
 * gave up — including ones whose wave never finished because a sibling probe
 * hung.
 */
const probeForMultiplier = async (
  storeType: string,
  candidates: TaxSample[],
  probes: ProbeResult[],
): Promise<number | null> => {
  const waves = [
    candidates.slice(0, CORROBORATION_QUORUM),
    candidates.slice(CORROBORATION_QUORUM),
  ].filter((wave) => wave.length > 0);

  let probedSoFar = 0;
  for (const wave of waves) {
    const firstSlot = probedSoFar;
    probedSoFar += wave.length;

    await Promise.all(
      wave.map(async (candidate, index) => {
        probes[firstSlot + index] = await measureCandidate(
          storeType,
          candidate,
        );
      }),
    );

    const corroborated = firstCorroborated(probes);
    if (corroborated !== null) {
      // storeTaxWithExpiry drops anything outside the believable band, so an
      // outlier is still returned (and rendered) but never cached.
      storeTaxWithExpiry(corroborated);
      return corroborated;
    }
  }

  return null;
};

/** Sentinel so a timed-out race is never confused with a real `null` result. */
const CALIBRATION_TIMED_OUT = Symbol('tax-calibration-timed-out');

/**
 * Run calibration, but never let it hold the UI longer than its budget.
 *
 * searchProducts awaits this before it returns results, so every millisecond
 * here is a millisecond the shopper stares at a spinner after the search API has
 * already answered.
 */
const calibrateWithinBudget = async (
  storeType: string,
  candidates: TaxSample[],
  probes: ProbeResult[],
): Promise<number | null> => {
  const probing = probeForMultiplier(storeType, candidates, probes);
  // We may stop awaiting this below; make sure it can never surface as an
  // unhandled rejection once nothing is listening.
  probing.catch(() => undefined);

  let timer: ReturnType<typeof setTimeout> | undefined;
  const budget = new Promise<typeof CALIBRATION_TIMED_OUT>((resolve) => {
    timer = setTimeout(
      () => resolve(CALIBRATION_TIMED_OUT),
      TAX_CALIBRATION_BUDGET_MS,
    );
  });

  let outcome: number | null | typeof CALIBRATION_TIMED_OUT;
  try {
    outcome = await Promise.race([probing, budget]);
  } finally {
    clearTimeout(timer);
  }

  if (outcome === CALIBRATION_TIMED_OUT) {
    // Stop WAITING, don't stop probing: the in-flight wave still settles and
    // still persists a corroborated multiplier, so a merely-slow store
    // calibrates for the next search instead of stalling this one.
    console.warn(
      'Tax calibration exceeded its budget; rendering with whatever has been measured so far',
    );
  } else if (typeof outcome === 'number') {
    return outcome;
  }

  // On a timeout the sweep never got to check the measurements that DID land —
  // a probe that answered alongside one that hung still counts.
  const corroborated = firstCorroborated(probes);
  if (corroborated !== null) {
    storeTaxWithExpiry(corroborated);
    return corroborated;
  }

  // Nothing corroborated — the feed held a single product, the measurements
  // disagreed, or we ran out of budget. Use the first one for THIS page load
  // (still far better than rendering untaxed prices) but don't let an
  // uncorroborated value persist into later ones.
  const taken = takenMeasurements(probes);
  if (taken.length > 0) {
    console.warn(
      'Tax multiplier could not be corroborated by a second product; using it for this page only:',
      taken,
    );
    return taken[0];
  }

  return null;
};

export const getTaxWithRetry = async (
  storeType: string,
  samples: TaxSample[],
  maxCandidates: number = 5,
): Promise<number | null> => {
  // ✅ No taxes for provider stores - return 1 (100% of price, no additional tax)
  if (storeType === 'provider') {
    console.log('Provider store detected - no taxes applied');
    return 1;
  }

  const currentHost = window.location.hostname.toLowerCase();

  // ✅ Force tax = 1.15 for balsamalula.com
  if (currentHost.includes('balsamalula.com')) {
    const storedTax = getTaxWithExpiry();

    // If tax not found, invalid, or expired — store fixed one
    if (!storedTax || isNaN(storedTax) || storedTax !== 1.15) {
      console.log('Detected balsamalula.com — setting fixed tax = 1.15');
      localStorage.removeItem(TAX_STORAGE_KEY);
      localStorage.removeItem(TAX_TIMESTAMP_KEY);
      storeTaxWithExpiry(1.15);
    } else {
      console.log('balsamalula.com fixed tax already valid');
    }

    return 1.15;
  }

  const cachedTax = getTaxWithExpiry();

  if (
    cachedTax !== null &&
    !isNaN(cachedTax) &&
    isFinite(cachedTax) &&
    cachedTax >= 1
  ) {
    return cachedTax;
  }

  // A store that just failed to calibrate fails again for the same reason, and
  // the sweep costs a storefront round-trip per candidate on the critical path
  // of every search. Don't re-buy the same nothing — see TAX_FAILURE_TTL_MS.
  if (hasRecentCalibrationFailure()) {
    return null;
  }

  // Capped so a fully broken feed can't fan out into dozens of probes.
  const candidates = (samples || []).slice(0, Math.max(1, maxCandidates));
  const probes: ProbeResult[] = [];
  const tax = await calibrateWithinBudget(storeType, candidates, probes);

  if (tax === null) {
    console.warn('Could not calibrate tax from any candidate product');
    // Only remember a failure the store will REPEAT. Candidates that simply
    // never answered — the deleted-product 404s Salla feeds are full of — say
    // nothing about the store, and the next search samples different products
    // that may well calibrate fine. Suppressing those would trade a slow search
    // for ten minutes of untaxed prices.
    if (answeredButUncalibratable(probes)) {
      rememberCalibrationFailure();
    }
  }

  return tax;
};
